import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * Detail-page header. Identity-first hierarchy:
 *   primary       — h5/700, the human-readable identity (supplier, customer, etc.)
 *   slug          — monospace caption, the technical ID
 *   metadata      — small captions separated by dots
 *
 * Optional back button + status badge + right-side action bar.
 *
 * Each metadata item may carry an optional tooltip for clarifying a code.
 */
export function PageHeader({
  primary,
  slug,
  metadata = [],         // [{ label, tooltip? }]
  backTo,                // string route OR true (history -1)
  statusBadge,           // React node — typically <StatusBadge>
  actions,               // React node — right-side action bar
  dense = false,         // tightens vertical spacing
}) {
  const navigate = useNavigate();
  const onBack = () => {
    if (backTo === true) navigate(-1);
    else if (typeof backTo === 'string') navigate(backTo);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: dense ? 2 : 3 }}>
      {backTo && (
        <IconButton onClick={onBack} size="small" sx={{ mt: 0.25 }}>
          <BackIcon />
        </IconButton>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2, color: 'text.primary' }}>
          {primary}
        </Typography>
        {(slug || metadata.length > 0) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
            {slug && (
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: 11 }}>
                {slug}
              </Typography>
            )}
            {metadata.map((m, i) => (
              <React.Fragment key={i}>
                {(slug || i > 0) && <Typography variant="caption" color="text.disabled">·</Typography>}
                {m.tooltip ? (
                  <Tooltip title={m.tooltip} arrow>
                    <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled' }}>
                      {m.label}
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography variant="caption" color="text.secondary">{m.label}</Typography>
                )}
              </React.Fragment>
            ))}
          </Box>
        )}
      </Box>
      {statusBadge}
      {actions}
    </Box>
  );
}

export default PageHeader;
